import { z } from "zod";
/**
 * Invoice lifecycle states.
 *
 * Originally exposed as a plain TS literal union (V1.0); promoted to a Zod
 * enum in P15 so request/response Zod schemas (e.g. invoice list-query
 * filters, admin mark-paid bodies) can reuse the same constant. The TS
 * `InvoiceStatus` type is preserved via `z.infer` — downstream consumers
 * importing `type InvoiceStatus` are unaffected.
 */
// Exported for Drizzle pgEnum reuse + downstream API/UI iteration.
export const InvoiceStatusValues = [
    "draft",
    "issued",
    "sent",
    "paid",
    "overdue",
    "void",
];
export const InvoiceStatus = z.enum(InvoiceStatusValues);
// ---------- P15: Stripe invoicing + buyer-pays-invoice DTOs ----------
/**
 * `POST /v1/storefront/invoices/:id/payment-intent` response — buyer-initiated
 * invoice payment via Stripe PaymentElement.
 *
 * `clientSecret` feeds `<Elements/>` `options.clientSecret` exactly like the
 * V1.0 checkout flow. `methods` enumerates the PI's `payment_method_types`
 * (`card` always; `pay_by_bank` when the buyer's company has the Open Banking
 * rail enabled — see spec §4.2). UI uses this list to decide which tab
 * defaults to selected on PaymentElement mount.
 *
 * Kept as a Zod schema (not just an interface) because consumer SDKs in the
 * storefront use it to validate the server response on the buyer-pay path,
 * where a stale-cache mismatch would silently break Stripe init.
 */
export const InvoicePaymentIntentResponse = z.object({
    clientSecret: z.string(),
    paymentIntentId: z.string(),
    methods: z.array(z.enum(["card", "pay_by_bank"])),
});
/**
 * `POST /v1/admin/invoices/:id/mark-paid` body — operator manually marks an
 * invoice paid (e.g. bank transfer matched against AR statement).
 *
 * `amountMinor` is the full invoice amount in minor units (pence). Partial
 * payments are not supported in V1.5 — pass the exact `invoice.totalMinor`.
 * `reference` is a free-form audit string (operator-supplied AR/bank ref).
 * `receivedAt` defaults to server clock when omitted.
 *
 * Currency defaults to GBP — V1.5 is single-currency; the field exists
 * forward-looking for V2+ multi-currency support.
 */
export const MarkInvoicePaidBody = z.object({
    amountMinor: z.coerce.number().int().min(1),
    currency: z.string().length(3).default("GBP"),
    reference: z.string().min(1).max(200),
    receivedAt: z.string().datetime().optional(),
});
/**
 * `POST /v1/admin/invoices/merge` body — consolidate multiple draft/issued
 * invoices for the same buyer into a single PDF (HMRC §7.3.5). Source invoices
 * are voided; new merged invoice is allocated a fresh gap-free number.
 *
 * 2 ≤ N ≤ 50 — single-invoice "merge" is rejected (use issue normally); 50
 * is a practical upper bound for the PDF rendering pipeline. Server further
 * validates same-customer + same-currency + status compatibility.
 */
export const MergeInvoicesBody = z.object({
    invoiceIds: z.array(z.uuid()).min(2).max(50),
});
